// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the Admin door
//  ---------------------------------------------------------------------------
//  A sibling of md-organizer-door.ts and not a copy of it. That file answers
//  "is a machine writing right now?", so that a write can be attributed and so
//  that the AI Organizer can be kept away from a person's own words. This one
//  answers a different question: "is this ManDana Admin?" — authority the user
//  does not routinely hold, over the handful of things the app ships.
//
//  WHY IT EXISTS. The six built-in projects, the IQAMA pursuit and the ten
//  domains used to be un-deletable because they were not documents at all.
//  md-builtins.ts made them documents so that search could find them, which is
//  the bug it was written to fix — and in doing so it made them removable for
//  the first time. Four verbs therefore had to grow a question they never
//  needed before: delete, rename, change of domain, change of pursuit. Status,
//  priority, start and target are NOT among them. Where somebody stands on the
//  IQAMA ladder is their own word and always was.
//
//  IT IS AN INTENT BOUNDARY, NOT A SECURITY BOUNDARY. This runs in the browser.
//  Anybody with devtools can call mdAdminAs, precisely as they can call
//  mdOrganizerAs today. What it stops is an accident — a stray click, a tidy-up
//  that reached further than it meant to, an agent that was never granted the
//  authority to remove anything. What it cannot stop is a determined person,
//  and no amount of client code would change that: enforcement against intent
//  would have to live in server/sync.js, which has no notion of an admin today.
//  Saying this plainly here is the point. A gate whose limits are written down
//  is a gate; one whose limits are implied is a false promise.
//
//  TODAY MANDANA ADMIN IS MARWAN, acting through Claude, until there is an
//  admin tool. There is deliberately no stored flag, no role on an account and
//  no way to "become" admin and stay that way — the door opens for one
//  synchronous call and shuts behind it, including when that call throws. A
//  session that could leave it open would be a role, and a role is the thing
//  this is not yet.
// ════════════════════════════════════════════════════════════════════════════

type MdAdminActor = 'user' | 'admin';

(function (g: any) {
  'use strict';

  const USER: MdAdminActor = 'user';
  const ADMIN: MdAdminActor = 'admin';

  /** Who is writing right now. Module state rather than a threaded argument,
      for the same reason md-organizer-door.ts keeps its actor that way: an
      argument is a thing a call site can forget, and this is a thing no call
      site may forget. */
  let actor: MdAdminActor = USER;

  function mdAdminActor(): MdAdminActor { return actor; }

  /** The one-word question every guarded verb asks. */
  function mdAdminIs(): boolean { return actor === ADMIN; }

  /** Run one synchronous call as ManDana Admin.

      The `finally` is the whole function. A door left open because the call
      inside it threw is worse than no door at all, because it is a door
      everybody believes is shut. Nesting does not stack — an admin inside an
      admin is still one admin — and the actor is restored to WHAT IT WAS rather
      than to 'user', so an inner call cannot shut an outer one. */
  function mdAdminAs<T>(fn: () => T): T {
    if (typeof fn !== 'function') return undefined as unknown as T;
    const before = actor;
    actor = ADMIN;
    try { return fn(); } finally { actor = before; }
  }

  Object.assign(g, { mdAdminActor, mdAdminIs, mdAdminAs });

  // CommonJS escape hatch, following md-record.ts and md-organizer-door.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) mod.exports = { mdAdminActor, mdAdminIs, mdAdminAs };
})(typeof window !== 'undefined' ? window : globalThis);
